import styled from '@emotion/styled';
import { Modal, modalClasses, type ModalProps } from '@mui/material';
import type { ReactNode } from 'react';
import { memo } from 'react';
import useMeasure from 'react-use-measure';

import { type DrawerWidth, drawerWidths } from '../theme/drawerWidths';
import { themeVars } from '../theme/theme';
import type { FrontModalHeaderOutsideProps } from './components/FrontModalHeader';
import { FrontModalHeader } from './components/FrontModalHeader';

export interface FrontModalProps extends ModalProps, FrontModalHeaderOutsideProps {
  width: DrawerWidth;
  footer: ReactNode;
}

const FrontModalInternal = memo(
  ({
    children,
    width,
    title,
    subTitle,
    hideCloseButton,
    headerTextPosition,
    footer,
    ...props
  }: FrontModalProps) => {
    const [headerRef, { height: headerHeight }] = useMeasure();
    const [footerRef, { height: footerHeight }] = useMeasure();

    return (
      <StyledModal {...props}>
        <Paper data-width={width}>
          <FrontModalHeader
            ref={headerRef}
            title={title}
            subTitle={subTitle}
            onClose={() => props.onClose?.({}, 'escapeKeyDown')}
            hideCloseButton={hideCloseButton}
            textPosition={headerTextPosition}
          />
          <Content headerHeight={headerHeight} footerHeight={footerHeight}>
            {children}
          </Content>
          <Footer ref={footerRef}>{footer}</Footer>
        </Paper>
      </StyledModal>
    );
  },
);

const StyledModal = styled((props: Omit<FrontModalProps, 'width' | 'footer'>) => (
  <Modal {...props} />
))`
  & .${modalClasses.backdrop} {
    background-color: ${themeVars.colors.layerOverlay};
  }
`;

const sp = themeVars.spacings;

const Paper = styled('div')<{ ['data-width']: DrawerWidth }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${themeVars.colors.white};
  border-radius: 16px;
  max-height: calc(100% - ${sp.sp06} * 2);
  min-width: ${(p) => `min(calc(100% - ${sp.sp06} * 2), ${drawerWidths[p['data-width']]})`};
  max-width: ${(p) => `min(calc(100% - ${sp.sp06} * 2), ${drawerWidths[p['data-width']]})`};
`;

const Content = styled('div')<{ headerHeight: number; footerHeight: number }>`
  flex: 1;
  padding: 0 ${sp.sp06} calc(${sp.sp06} + ${(p) => p.footerHeight}px);
  max-height: ${(p) => `calc(100vh - ${themeVars.spacings.sp06} * 2 - ${p.headerHeight}px)`};
  overflow: scroll;
`;

const Footer = styled('div')`
  padding: ${sp.sp05} ${sp.sp06} ${sp.sp06};
  border-top: 1px solid ${themeVars.colors.coldGrey200};
  position: sticky;
  background-color: ${themeVars.colors.white};
  border-radius: 0 0 16px 16px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

export const FrontModal = Object.assign(FrontModalInternal, {
  Paper,
  Footer,
});
